import { Fragment, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { Portal } from "@headlessui/react";
import { Search, ArrowUp, ArrowDown, ChevronsUpDown, Columns3, ChevronDown } from "lucide-react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { dateToGroupKey, groupKeyLabel, eur, type GroupGranularity } from "../../lib/format";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export interface Column<T> {
  key: string;
  header: string;
  /** Rauwe waarde voor sortering + zoek-index. Geld → Number() via sortValue. */
  accessor: (row: T) => string | number;
  /** Optionele custom cel-render (Badge, €-opmaak, enz.). */
  render?: (row: T) => ReactNode;
  sortable?: boolean; // default true
  /** Numerieke/datum-sortering (geld → Number, datum → timestamp). */
  sortValue?: (row: T) => number | string;
  className?: string; // uitlijning th/td
  width?: string;
}

interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  searchPlaceholder?: string;
  initialSort?: { key: string; dir: "asc" | "desc" };
  toolbar?: ReactNode;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  /** Voor export: wordt aangeroepen met de huidige gefilterde+gesorteerde rijen. */
  onVisibleRowsChange?: (rows: T[]) => void;
  /** Opt-in rij-selectie (checkbox-kolom). */
  selectable?: boolean;
  /** Geselecteerde rijen (wanneer selectable). */
  onSelectionChange?: (rows: T[]) => void;
  /** Kolom-keys die standaard verborgen zijn (kolom-tonen/verbergen-toggle). */
  defaultHiddenColumns?: string[];
  /** Stabiele sleutel om de kolom-keuze in localStorage te bewaren, zodat de
   *  verborgen kolommen bewaard blijven bij navigeren weg en terug. */
  storageKey?: string;
  /** Groepeer de rijen op een datumveld in uitklapbare maand/kwartaal/jaar-groepen.
   *  Wanneer gezet krijgt de tabel een granularity-toggle en groep-scheidingsrijen.
   *  Pagina's die dit niet meegeven merken geen verschil (nul regressie). */
  groupBy?: {
    getDate: (row: T) => string | number | Date | null | undefined;
    /** Optioneel: bedrag per rij voor een subttotaal in de groep-header. */
    getAmount?: (row: T) => number | string | null | undefined;
  };
  /** Start-granulariteit van de groepering (default "month"). */
  defaultGranularity?: GroupGranularity;
}

type SortDir = "asc" | "desc" | null;

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  searchPlaceholder = "Zoeken...",
  initialSort,
  toolbar,
  emptyMessage = "Geen resultaten.",
  onRowClick,
  onVisibleRowsChange,
  selectable,
  onSelectionChange,
  defaultHiddenColumns,
  storageKey,
  groupBy,
  defaultGranularity = "month",
}: DataTableProps<T>) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(initialSort?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initialSort?.dir ?? null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [granularity, setGranularity] = useState<GroupGranularity>(defaultGranularity);
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
    // Bewaar de kolom-keuze in localStorage (per tabel), zodat navigeren weg en
    // terug de verborgen kolommen niet reset. Valt terug op defaultHiddenColumns.
    if (storageKey) {
      try {
        const saved = localStorage.getItem(`aiboek:cols:${storageKey}`);
        if (saved) return new Set(JSON.parse(saved));
      } catch {
        /* negeer corrupte JSON */
      }
    }
    return new Set(defaultHiddenColumns ?? []);
  });

  // Schrijf de verborgen kolommen weg naar localStorage wanneer ze wijzigen.
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`aiboek:cols:${storageKey}`, JSON.stringify([...hiddenKeys]));
    } catch {
      /* storage vol of uitgeschakeld — negeer */
    }
  }, [storageKey, hiddenKeys]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenKeys.has(c.key)),
    [columns, hiddenKeys]
  );

  // Zoeken over alle accessors (textuele match).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      columns.some((col) => String(col.accessor(row) ?? "").toLowerCase().includes(q))
    );
  }, [rows, columns, query]);

  // Sorteren (asc → desc → none cyclus).
  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = col.sortValue ? col.sortValue(a) : col.accessor(a);
      const bv = col.sortValue ? col.sortValue(b) : col.accessor(b);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av ?? "").localeCompare(String(bv ?? ""), "nl-NL") * dir;
    });
  }, [filtered, columns, sortKey, sortDir]);

  // Groeperen van de gesorteerde rijen op maand/kwartaal/jaar. Sleutels sorteren
  // lexicografisch aflopend (nieuwste eerst); "Zonder datum" valt altijd onderaan.
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map<string, T[]>();
    for (const row of sorted) {
      const key = dateToGroupKey(groupBy.getDate(row), granularity);
      const arr = map.get(key);
      if (arr) arr.push(row);
      else map.set(key, [row]);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === "Zonder datum") return 1;
      if (b === "Zonder datum") return -1;
      return b.localeCompare(a, "nl-NL");
    });
    return { keys, map };
  }, [groupBy, sorted, granularity]);

  const toggleGroup = (key: string) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Alleen de parent inlichten als de zichtbare rijen ÉCHT veranderen (op basis van
  // een signatuur van sleutels). Voorkomt een oneindige render-loop wanneer de
  // parent telkens nieuwe array-refs doorgeeft (inline gedefinieerde kolommen e.d.).
  const lastSig = useRef("");
  useEffect(() => {
    if (!onVisibleRowsChange) return;
    const sig = sorted.map(rowKey).join("");
    if (sig === lastSig.current) return;
    lastSig.current = sig;
    onVisibleRowsChange(sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  // ── Rij-selectie ──
  const visibleKeys = sorted.map(rowKey);
  const allVisibleSelected =
    selectable && visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.has(k));

  const emitSelection = (keys: Set<string>) => {
    if (!onSelectionChange) return;
    onSelectionChange(rows.filter((r) => keys.has(rowKey(r))));
  };

  const toggleAll = () => {
    const next = new Set(selectedKeys);
    if (allVisibleSelected) visibleKeys.forEach((k) => next.delete(k));
    else visibleKeys.forEach((k) => next.add(k));
    setSelectedKeys(next);
    emitSelection(next);
  };

  const toggleRow = (row: T) => {
    const k = rowKey(row);
    const next = new Set(selectedKeys);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSelectedKeys(next);
    emitSelection(next);
  };

  const toggleSort = (col: Column<T>) => {
    if (col.sortable === false) return;
    if (sortKey !== col.key) {
      setSortKey(col.key);
      setSortDir("asc");
      return;
    }
    // zelfde kolom: asc → desc → none
    if (sortDir === "asc") setSortDir("desc");
    else if (sortDir === "desc") {
      setSortDir(null);
      setSortKey(null);
    } else setSortDir("asc");
  };

  const sortIcon = (col: Column<T>) => {
    if (col.sortable === false) return null;
    if (sortKey !== col.key || !sortDir) return <ChevronsUpDown size={16} className="text-slate-300" />;
    return sortDir === "asc" ? (
      <ArrowUp size={16} className="text-brand-500" />
    ) : (
      <ArrowDown size={16} className="text-brand-500" />
    );
  };

  const toggleCol = (key: string) =>
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Rendeert één gegevensrij (hergebruikt in zowel de platte als de gegroepeerde view).
  const renderRow = (row: T) => {
    const k = rowKey(row);
    const checked = selectedKeys.has(k);
    return (
      <tr
        key={k}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        className={cn(
          "border-b border-slate-100 last:border-0",
          onRowClick && "cursor-pointer",
          checked ? "bg-brand-50/40" : "hover:bg-slate-50/60"
        )}
      >
        {selectable && (
          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              aria-label="Rij selecteren"
              checked={checked}
              onChange={() => toggleRow(row)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
          </td>
        )}
        {visibleColumns.map((col) => (
          <td key={col.key} className={cn("px-4 py-3 text-slate-700", col.className)}>
            {col.render ? col.render(row) : String(col.accessor(row) ?? "")}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="search-chip flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={21} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 md:ml-auto">
          {groupBy && (
            <GranularityToggle value={granularity} onChange={setGranularity} />
          )}
          <ColumnToggle
            columns={columns}
            hiddenKeys={hiddenKeys}
            onToggle={toggleCol}
            onShowAll={() => setHiddenKeys(new Set())}
          />
          {toolbar}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-100">
            <tr>
              {selectable && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    aria-label="Alles selecteren"
                    checked={!!allVisibleSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                </th>
              )}
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col)}
                  className={cn(
                    "px-4 py-3 text-left font-semibold text-slate-600 select-none",
                    col.sortable !== false && "cursor-pointer hover:text-slate-800",
                    col.className
                  )}
                  style={col.width ? { width: col.width } : undefined}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {col.header}
                    {sortIcon(col)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped ? (
              grouped.keys.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="px-4 py-10 text-center text-slate-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                grouped.keys.map((key) => {
                  const rows = grouped.map.get(key)!;
                  const collapsed = collapsedKeys.has(key);
                  const subtotal = groupBy!.getAmount
                    ? rows.reduce((s, r) => s + (Number(groupBy!.getAmount!(r)) || 0), 0)
                    : null;
                  return (
                    <Fragment key={key}>
                      <tr
                        onClick={() => toggleGroup(key)}
                        className="bg-slate-100/80 border-y border-slate-200 cursor-pointer hover:bg-slate-100 select-none"
                      >
                        <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-2 font-semibold text-slate-700">
                            <ChevronDown
                              size={18}
                              className={cn("text-slate-400 transition-transform", collapsed && "-rotate-90")}
                            />
                            {groupKeyLabel(key, granularity)}
                            <span className="text-xs font-normal text-slate-400">
                              {rows.length} {rows.length === 1 ? "regel" : "regels"}
                            </span>
                            {subtotal !== null && (
                              <span className="ml-auto text-sm font-bold text-brand-700">
                                {eur(subtotal)}
                              </span>
                            )}
                          </span>
                        </td>
                      </tr>
                      {!collapsed && rows.map((row) => renderRow(row))}
                    </Fragment>
                  );
                })
              )
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="px-4 py-10 text-center text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sorted.map((row) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Dropdown om kolommen te tonen/verbergen. Panel in een Portal (position:fixed)
 *  zodat het niet wordt afknapt; klikbuiten/backdrop sluit het. */
function ColumnToggle<T>({
  columns,
  hiddenKeys,
  onToggle,
  onShowAll,
}: {
  columns: Column<T>[];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
  onShowAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    // Sluit het paneel bij scrollen op de pagina, maar NIET bij scrollen binnen
    // het paneel zelf (dat is het overflow-y-auto lijstje hieronder).
    const onScroll = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open]);

  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const left = Math.min(r.left, window.innerWidth - 256 - 8);
      setCoords({ top: r.bottom + 6, left: Math.max(8, left) });
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={openMenu}
        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50 hover:border-slate-300"
        title="Kolommen tonen/verbergen"
      >
        <Columns3 size={16} />
        Kolommen
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <Portal>
          {/* backdrop: klik buiten sluit het paneel */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            style={{ position: "fixed", top: `${coords.top}px`, left: `${coords.left}px`, zIndex: 50 }}
            className="w-64 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Kolommen</span>
              {hiddenKeys.size > 0 && (
                <button onClick={onShowAll} className="text-xs font-semibold text-brand-600 hover:text-brand-700">
                  Alles tonen
                </button>
              )}
            </div>
            {columns.map((col) => {
              const visible = !hiddenKeys.has(col.key);
              return (
                <label
                  key={col.key}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => onToggle(col.key)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  {col.header}
                </label>
              );
            })}
          </div>
        </Portal>
      )}
    </div>
  );
}

/** Segmented control om de groeperings-granulariteit te kiezen (maand/kwartaal/jaar). */
function GranularityToggle({
  value,
  onChange,
}: {
  value: GroupGranularity;
  onChange: (g: GroupGranularity) => void;
}) {
  const opts: { key: GroupGranularity; label: string }[] = [
    { key: "month", label: "Maand" },
    { key: "quarter", label: "Kwartaal" },
    { key: "year", label: "Jaar" },
  ];
  return (
    <div className="inline-flex items-center rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
      {opts.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={cn(
            "rounded-lg px-3 py-1.5 text-sm font-medium transition-all",
            value === o.key
              ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-sm"
              : "text-slate-600 hover:text-slate-900"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}